import { animated, useTransition } from "@react-spring/web";
import { ArrowLeft } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useFragment, useLazyLoadQuery } from "react-relay";
import { Outlet, useNavigate, useParams, useSearchParams } from "react-router";
import { appMeFragment,type MeUserDataType } from "src/app";
import { cn } from "src/common/utils";
import { EvaluationBanner } from "src/components/EvaluationBanner";
import { Header } from "src/components/header";
import { LeftNavigation } from "src/components/left-navigation";
import { PageSuspense } from "src/components/ui/page-suspense";
import { Toaster } from "src/components/ui/toaster";
import { UITourBanner } from "src/components/ui/UITourBanner";
import { useMe } from "src/context/MeContext";
import { NavigationProvider, useNavigation } from "src/context/NavigationContext";
import { useMarkConversationReadMutation } from "src/pages/AskPage/hooks/useMarkConversationReadMutation";
import { getProductName } from "src/utils/brandTheme";

import DeleteConversationDialog from "../AskPage/components/DeleteConversationDialog";
import LlmConversationSidebar from "../AskPage/components/LlmConversationSidebar";
import { useLlmConversationManagement } from "../AskPage/hooks/useLlmConversationActions";
import { ChatsPageQuery } from "./queries/ChatsPageQuery";

function ChatsPageContent() {
  const [refreshKey, setRefreshKey] = useState(0);

  const data = useLazyLoadQuery(ChatsPageQuery, {}, { fetchPolicy: "store-and-network", fetchKey: refreshKey }) as any;

  const refetch = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  return <ChatsPageLoadedContent data={data} refetch={refetch} />;
}

function ChatsPageLoadedContent({ data, refetch }: { data: any; refetch: () => void }) {
  const navigate = useNavigate();
  const params = useParams<{ conversationId?: string }>();
  const conversationId = params.conversationId;
  const [searchParams] = useSearchParams();
  const onboardingStep = searchParams.get("onboarding");
  const isOnboarding = onboardingStep !== null;
  const { isMobile, setMobileConversationHeader } = useNavigation();
  const me = useMe();
  const productName = getProductName(me.organization?.brandTheme);

  // Use fragment to get user data including pictureUrl, firstName, lastName
  const meData = useFragment(appMeFragment, data.me);

  const currentConversationIdRef = useRef(conversationId);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const conversationRefetchFns = useRef<Map<string, () => void>>(new Map());
  const mainContentRef = useRef<HTMLDivElement>(null);

  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);


  // Set mobile conversation header when viewing a conversation or new chat
  const isNewChat = searchParams.get("new") === "true";
  useEffect(() => {
    if (isMobile && (conversationId || isNewChat)) {
      setMobileConversationHeader({
        title: isNewChat ? "New Chat" : "Chat",
        onBack: () => navigate("/chat"),
      });
    } else {
      setMobileConversationHeader(null);
    }
    return () => setMobileConversationHeader(null);
  }, [isMobile, conversationId, isNewChat, navigate, setMobileConversationHeader]);

  useEffect(() => {
    currentConversationIdRef.current = conversationId;
  }, [conversationId]);

  // Scroll to top when in onboarding mode and conversation changes
  // This ensures the user can see the onboarding banner
  // Use a delay to override any scroll behavior from child components
  useEffect(() => {
    if (!isOnboarding || !conversationId || !mainContentRef.current) {
      return;
    }
    const scrollToTop = () => {
      if (mainContentRef.current) {
        mainContentRef.current.scrollTo({ top: 0, behavior: "instant" });
      }
    };
    // Immediate scroll
    scrollToTop();
    // Delayed scroll to override child component scrolls
    const timeoutId = setTimeout(scrollToTop, 100);
    return () => clearTimeout(timeoutId);
  }, [isOnboarding, conversationId]);

  const setConversationRefetch = useCallback((conversationId: string, refetchFn: () => void) => {
    if (refetchFn && refetchFn.toString() !== "() => {}") {
      conversationRefetchFns.current.set(conversationId, refetchFn);
    } else {
      conversationRefetchFns.current.delete(conversationId);
    }
  }, []);

  const { markAsRead, markedReadIds } = useMarkConversationReadMutation();

  const handleCreateNewConversation = () => {
    // On mobile, add query param to show new chat view instead of list
    navigate(isMobile ? "/chat?new=true" : "/chat");
  };

  const handleSelectConversation = (id: string) => {
    refetch();
    navigate(`/chat/c/${id}`);
  };

  const {
    handleDeleteConversation,
    confirmDeleteConversation,
    handleRenameConversation,
    isDeleting,
    deletingConversationId,
    deletingConversationTitle,
    setDeletingConversationId,
    setDeletingConversationTitle,
  } = useLlmConversationManagement({
    refetchConversations: refetch,
  });

  // Handle opening delete dialog
  const onDeleteConversation = (id: string, title: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const { conversationId, title: returnedTitle } = handleDeleteConversation(id, title, e);
    setDeletingConversationId(conversationId);
    setDeletingConversationTitle(returnedTitle);
    setDeleteDialogOpen(true);
  };

  // Handle confirming deletion
  const onConfirmDelete = () => {
    if (deletingConversationId) {
      confirmDeleteConversation(deletingConversationId);
    }
    setDeleteDialogOpen(false);
  };

  // Handle canceling deletion
  const onCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeletingConversationId(null);
    setDeletingConversationTitle("");
  };

  const sidebarContent = (
    <LlmConversationSidebar
      me={data.me}
      conversationId={conversationId}
      onCreateNewConversation={handleCreateNewConversation}
      onSelectConversation={handleSelectConversation}
      onDeleteConversation={onDeleteConversation}
      onRenameConversation={handleRenameConversation}
      onRefetch={refetch}
      markedReadIds={markedReadIds}
    />
  );

  // On mobile without a conversation and not explicitly creating new chat, show chat list
  const isNewChatMode = searchParams.get("new") === "true";
  const showMobileChatList = isMobile && !conversationId && !isNewChatMode;

  // Track previous state for animation direction and initial mount
  const prevShowChatListRef = useRef(showMobileChatList);
  const isInitialMount = useRef(true);

  // Calculate slide direction synchronously (not via state) so it's correct on first render
  // Forward = going from list to conversation (prevShowList was true, now false)
  // Back = going from conversation to list (prevShowList was false, now true)
  const slideDirection = prevShowChatListRef.current && !showMobileChatList ? "forward" : "back";

  // After mount, allow animations for subsequent navigations
  useEffect(() => {
    isInitialMount.current = false;
  }, []);

  // Update ref after render so next navigation knows the previous state
  useEffect(() => {
    prevShowChatListRef.current = showMobileChatList;
  }, [showMobileChatList]);

  // React Spring transition for mobile views
  const mobileTransitions = useTransition(showMobileChatList, {
    from: {
      transform: slideDirection === "forward" ? "translateX(100%)" : "translateX(-100%)",
      opacity: 0,
    },
    enter: {
      transform: "translateX(0%)",
      opacity: 1,
    },
    leave: {
      transform: slideDirection === "forward" ? "translateX(-100%)" : "translateX(100%)",
      opacity: 0,
    },
    config: { tension: 300, friction: 30 },
    immediate: isInitialMount.current,
  });

  const conversationContent = (
    <>
      {/* Onboarding Banner */}
      {isOnboarding && (
        <div className="p-6 pb-0 relative z-10">
          <UITourBanner
            text={conversationId
              ? `Congrats on your first chat with ${productName}! You can view ${productName}'s response below.`
              : `Press 'Enter' to ask ${productName} about errors that occurred in your system.`}
            onboardingStep={onboardingStep ? parseInt(onboardingStep, 10) : undefined}
            showBackButton={!!conversationId}
          />
        </div>
      )}

      <Outlet
        context={{
          userData: {
            pictureUrl: meData?.pictureUrl || undefined,
            firstName: meData?.firstName,
            lastName: meData?.lastName,
          },
          messagesEndRef,
          refreshSignal: 0,
          tips: data.me?.organization?.uiTips || [],
          setConversationRefetch,
          refetchSidebar: refetch,
          markedReadIds,
          markConversationAsRead: markAsRead,
          isOnboarding,
        }}
      />
    </>
  );

  return (
    <div className="flex w-full h-full">
      {/* Delete Confirmation Dialog */}
      <DeleteConversationDialog
        isOpen={deleteDialogOpen}
        onClose={onCancelDelete}
        conversationTitle={deletingConversationTitle}
        onConfirm={onConfirmDelete}
        isDeleting={isDeleting}
      />

      {/* Desktop: show sidebar */}
      {!isMobile && (
        <div className="px-4 pt-2 bg-background border-r border-border w-[280px] min-w-[280px] h-full">
          {sidebarContent}
        </div>
      )}

      {/* Main content area */}
      <div className="flex-1 flex flex-col h-full relative max-h-full w-full">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(59,130,246,0.08)_0%,rgba(0,0,0,0)_50%)] pointer-events-none"></div>

        {/* Content area */}
        <div ref={mainContentRef} className={cn("flex-1 flex flex-col relative", isMobile ? "overflow-hidden" : "overflow-y-auto overflow-x-hidden")}>
          {isMobile ? (
            // Mobile: animated sliding views
            <div className="relative flex-1 overflow-hidden">
              {mobileTransitions((style, showList) =>
                showList ? (
                  <animated.div
                    style={style}
                    className="absolute inset-0 px-4 pt-2 overflow-y-auto bg-background"
                  >
                    {sidebarContent}
                  </animated.div>
                ) : (
                  <animated.div
                    style={style}
                    className="absolute inset-0 flex flex-col bg-background overflow-hidden"
                  >
                    {conversationContent}
                  </animated.div>
                )
              )}
            </div>
          ) : (
            // Desktop: no animation
            conversationContent
          )}
        </div>
      </div>
    </div>
  );
}

function ChatsContentWrapper() {
  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <div className="p-6 text-center">
          <h3 className="text-xl font-semibold mb-2">Error Loading Chats</h3>
          <p className="text-red-500">{error.message || "An error occurred while loading your conversations."}</p>
        </div>
      )}
    >
      <PageSuspense>
        <ChatsPageContent />
      </PageSuspense>
    </ErrorBoundary>
  );
}

function MobileConversationHeaderBar() {
  const { mobileConversationHeader } = useNavigation();

  if (!mobileConversationHeader) return null;

  return (
    <nav className="h-11 bg-background shadow-lg relative">
      <div className="absolute left-0 right-0 bottom-0 h-[1px] bg-border shadow-[0_0_5px_rgba(0,0,0,0.3)]"></div>
      <div className="h-full mx-auto flex w-full items-center px-2">
        <button
          onClick={mobileConversationHeader.onBack}
          className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-card focus:outline-none transition-colors ml-1.5 mr-3"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <span className="text-base font-medium text-foreground truncate">
          {mobileConversationHeader.title}
        </span>
      </div>
    </nav>
  );
}

function ChatsPageLayoutContent({ page, me }: { page: React.ReactElement; me: MeUserDataType }) {
  const { isLeftNavCollapsed, isMobile, mobileConversationHeader } = useNavigation();

  const showMobileConversationHeader = isMobile && mobileConversationHeader;

  return (
    <div className="h-screen flex bg-background overflow-hidden">
      <LeftNavigation isCollapsed={isLeftNavCollapsed} />
      <div className="flex flex-col flex-1 h-screen">
        {!showMobileConversationHeader && <Header me={me} />}
        {showMobileConversationHeader && <MobileConversationHeaderBar />}
        <EvaluationBanner suspendedAt={me.organization?.suspendedAt ?? null} />
        <main className="flex-1 flex flex-col h-full relative max-h-full overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br dark:from-slate-900/5 dark:to-slate-950/10 pointer-events-none"></div>
          {page}
        </main>
      </div>
      <Toaster />
    </div>
  );
}

export const getChatsPageLayout = (page: React.ReactElement, me: MeUserDataType) => {
  return (
    <NavigationProvider>
      <ChatsPageLayoutContent page={page} me={me} />
    </NavigationProvider>
  );
};

const ChatsPage = Object.assign(ChatsContentWrapper, {
  getLayout: getChatsPageLayout,
});

export default ChatsPage;
